import {useMemo} from 'react';
import {useInfiniteQuery} from '@tanstack/react-query';
import gigsApi from '@/api/gigs';
import {PaginationParams, PaginationResponse} from '@/api/pagination';
import paginationUtils from '@/hooks/queries/common/pagination';
import {AvailableGigsResponse} from '@/api/gigs/types';
import {
  QUERY_STALE_TIME,
  FilterableGigsQueryOptions,
  getGigsQueryKey,
} from '../common';
import mapUserCampaignListItemDtoToAvailableGig from './dataMapper';

export type UseAvailableGigsQueryOptions = FilterableGigsQueryOptions;

/**
 * Hook for fetching a list of gigs with filtering and pagination.
 * Uses infinite query to support loading more data as user scrolls.
 */
export default function useAvailableGigsQuery(
  options: UseAvailableGigsQueryOptions = {}
) {
  const {enabled = true, params = {}} = options;

  // Use infinite query for paginated gigs data
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch,
  } = useInfiniteQuery<AvailableGigsResponse>({
    queryKey: getGigsQueryKey('available', params, options.include),
    queryFn: async ({pageParam = 1}) => {
      const pagination: PaginationParams = {
        page: pageParam as number,
      };
      return gigsApi.availableGigsList(params, pagination, options.include);
    },
    getNextPageParam: paginationUtils.getNextPageParam,
    enabled,
    refetchOnWindowFocus: false,
    staleTime: QUERY_STALE_TIME,
  });

  // Map each DTO to UI model and flatten all pages of gigs into a single array
  const allGigs = useMemo(() => {
    if (!data?.pages) {
      return [];
    }
    return data.pages.flatMap((page) =>
      page.data.map(mapUserCampaignListItemDtoToAvailableGig)
    );
  }, [data?.pages]);

  // Get the last page's pagination info
  const paginationInfo =
    data?.pages?.[data.pages.length - 1]?.pagination ||
    ({} as PaginationResponse);

  return {
    gigs: allGigs,
    pagination: paginationInfo,
    isLoading,
    isError,
    error,
    refetch,
    count: paginationInfo.totalEntries,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
}
